/*
 * @Author: yong.wang webwang1@outlook.com
 * @Date: 2026-08-06 16:32:58
 * @LastEditTime: 2026-09-01 14:07:41
 * @LastEditors: yong.wang webwang1@outlook.com
 * @Description: 文件说明
 * Copyright (c) 2026 by webwang1, All Rights Reserved.
 */
import utility from '@/assets/utils/utility';
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import store from '../store';

// reset password error and redirect to reset password page
if (window.location.href.indexOf('error_description=AADB2C90118') > -1) {
  var redirectUrl = process.env.VUE_APP_AAD_B2C_REDIRECT_URI
  if (redirectUrl.substr(redirectUrl.length - 1) == '/') {
    window.location.href = process.env.VUE_APP_AAD_B2C_REDIRECT_URI + "401?reset_pwd=1";
  }
  else {
    window.location.href = process.env.VUE_APP_AAD_B2C_REDIRECT_URI + "/401?reset_pwd=1";
  }
}

// redirect to index page if changed password
if (window.location.href.indexOf('error_description=AADB2C90091') > -1) {
  window.location.href = process.env.VUE_APP_AAD_B2C_REDIRECT_URI;
}

// redirect to index page if registration finish
if (window.location.href.indexOf('#id_token=') > -1) {
  redirectUrl = process.env.VUE_APP_AAD_B2C_REDIRECT_URI
  if (redirectUrl.substr(redirectUrl.length - 1) == '/') {
    window.location.href = process.env.VUE_APP_AAD_B2C_REDIRECT_URI + "401?new_user=1";
  }
  else {
    window.location.href = process.env.VUE_APP_AAD_B2C_REDIRECT_URI + "/401?new_user=1";
  }
}

// NOTE: 正常登录授权码回调的 URL 中也会包含 code= 参数（可能在 hash 或 search 中），
//       所以绝对不能仅凭 URL 包含 "?code=" 就判定为 "修改密码完成" 场景，否则会中断登录流程。
//       区分改密码完成 vs 正常登录：改密码完成后会走 AADB2C90091 分支（上面 L25-L27），
//       或者通过 policy / error 参数区分，不能直接拦截所有带 code= 的 URL。

// https://www.npmjs.com/package/@azure/msal-browser
//
// AADSTS50049 修复策略（与旧版 MSAL v1.4.3 行为对齐，参考运行正常的网络截图）：
// ① knownAuthorities 配置 B2C 域名  →  MSAL 直接跳过 login.microsoftonline.com 实例发现网络请求
//    （MSAL 官方文档推荐做法，源码中命中后直接 createCloudDiscoveryMetadataFromHost，100% 不走网络）
// ② 不提供 authorityMetadata  →  让 MSAL 主动请求 B2C 的 {authority}/v2.0/.well-known/openid-configuration
//    （旧版 MSAL v1.4.3 正是这么做的，截图中该请求返回 200 OK，比手动构造 URL 更可靠）
// ③ cloudDiscoveryMetadata 作为 knownAuthorities 的后备（双重保险）
//
const authAuthority = process.env.VUE_APP_AAD_B2C_AUTHORITY || '';
const authClientId = process.env.VUE_APP_AAD_B2C_CLIENT_ID || '';
const authRedirectUri = process.env.VUE_APP_AAD_B2C_REDIRECT_URI || '';
const authScopeRaw = process.env.VUE_APP_AAD_B2C_SCOPE || '';

// ---------- 早期配置校验：避免空配置导致 MSAL 静默回退到 AAD 默认值 ----------
const missingConfig = [];
if (!authAuthority) missingConfig.push('VUE_APP_AAD_B2C_AUTHORITY');
if (!authClientId) missingConfig.push('VUE_APP_AAD_B2C_CLIENT_ID');
if (!authRedirectUri) missingConfig.push('VUE_APP_AAD_B2C_REDIRECT_URI');
if (!authScopeRaw) missingConfig.push('VUE_APP_AAD_B2C_SCOPE');
if (missingConfig.length > 0) {
  const msg = `[MSAL] 缺少必需的环境变量: ${missingConfig.join(', ')}。` +
    `\n请检查 .env.{dev|test|sit|uat|prd} 文件（sit/uat/prd 当前仅含 TEST_VALUE，完全没有 B2C 配置）。` +
    `\n若继续运行，MSAL 将回退到默认 authority（login.microsoftonline.com/common），必然触发认证错误。`;
  throw new Error(msg);
}

// ---------- 解析 authority URL ----------
let authorityHost;
try {
  const authorityUrl = new URL(authAuthority);
  authorityHost = authorityUrl.host.toLowerCase(); // URL 构造器已标准化为小写，这里确保一致性
} catch (e) {
  const msg = `[MSAL] VUE_APP_AAD_B2C_AUTHORITY 不是合法的 URL: "${authAuthority}"。` +
    `\n正确格式示例: https://{tenant}.b2clogin.com/{tenant}.onmicrosoft.com/{policyName}`;
  throw new Error(msg, { cause: e });
}
if (!authorityHost) {
  const msg = `[MSAL] authority 无法解析主机名: "${authAuthority}"`;
  throw new Error(msg);
}

// ---------- knownAuthorities（主力方案：跳过实例发现）----------
// knownAuthorities 仅需 hostname，不含协议或路径
// 额外加入若干 b2clogin.com 通用变体，增强健壮性
const knownAuthorities = Array.from(new Set(
  [
    authorityHost,
    authorityHost.includes('b2clogin.com') ? authorityHost : null
  ].filter(Boolean)
));

// ---------- cloudDiscoveryMetadata（后备方案，双重保险）----------
const cloudDiscoveryMetadata = JSON.stringify({
  metadata: [
    {
      aliases: [authorityHost],
      preferred_cache: authorityHost,
      preferred_network: authorityHost
    }
  ]
});

// ---------- 配置汇总 ----------
// 注意：这里**不设置 authorityMetadata**，让 MSAL 从网络获取真实的 OIDC 配置
//       （与旧版 MSAL v1.4.3 行为一致，截图证明该请求 200 OK）
const msalConfig = {
  auth: {
    clientId: authClientId,
    authority: authAuthority,
    redirectUri: authRedirectUri,
    postLogoutRedirectUri: authRedirectUri,
    knownAuthorities: knownAuthorities,
    cloudDiscoveryMetadata: cloudDiscoveryMetadata
    // authorityMetadata: 不设置 → MSAL 自动发起 {authority}/v2.0/.well-known/openid-configuration 请求
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true
  }
};

// ---------- 创建 MSAL 实例 ----------
const msalInstance = new PublicClientApplication(msalConfig);

const loginRequest = {
  scopes: authScopeRaw.split('|').filter(Boolean)
};

msalInstance.loginRequest = loginRequest;

// MSAL v3 requires async initialization
let initialized = false;
let initPromise = null;

// 提供一个可以安全多次调用的初始化方法
function ensureInitialized() {
  if (!initPromise) {
    initPromise = msalInstance
      .initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then((response) => {
        initialized = true;
        return response;
      });
  }
  return initPromise;
}

msalInstance.waitForRedirect = ensureInitialized;

// 启动初始化
ensureInitialized();

// ---------- 通用的 URL 参数清理：同时支持 search(?...) 与 hash(#...) 模式 ----------
// Azure AD B2C 授权码回调通常用 hash 传参（#code=...&state=...），
// 早期老版本或部分配置可能用 search（?code=...&state=...）。两者都要清干净。
function cleanAuthParamsFromUrl() {
  let needClean = false;
  // search 形式：?code= / ?state=
  if (
    window.location.search.includes('code=') ||
    window.location.search.includes('state=')
  ) {
    needClean = true;
  }
  // hash 形式：#code= / #state= / #xxx&code= / #xxx?code=
  const rawHash = window.location.hash || '';
  if (
    rawHash.includes('code=') ||
    rawHash.includes('state=') ||
    rawHash.includes('client_info=')
  ) {
    needClean = true;
  }
  if (needClean) {
    // 清掉 search 和 hash 两部分所有 B2C 回调参数，只保留干净的 pathname
    window.history.replaceState(null, '', window.location.pathname);
  }
}

// ---------- 通用：用 account 静默获取业务 accessToken，并保存到 store + 拉取用户信息 ----------
// 统一封装 handleToken / 登录回调 / 交互完成后的"拿 token + 填 store + getUserInfo"三段式逻辑，
// 避免各处复制粘贴导致行为不一致。
// 失败时，若是 InteractionRequiredAuthError 则回退 acquireTokenRedirect，其他错误仅静默处理。
function acquireTokenSilentAndSetup(account) {
  return msalInstance
    .acquireTokenSilent({ ...loginRequest, account: account })
    .then((response) => {
      if (response && response.accessToken) {
        store.dispatch('setSignininfo', response.accessToken);
      }
      // 无论 accessToken 是否存在，只要 store 里有 Signin_info 就尝试拉用户信息
      // （如果 redirect 回调中已保存了 token，这里就能用它调 getUserInfo）
      const currentToken = store.getters.get_Signin_info;
      if (currentToken) {
        utility.getUserInfo();
      }
      return response;
    })
    .catch((err) => {
      if (err instanceof InteractionRequiredAuthError) {
        if (err.errorCode === 'interaction_in_progress') {
          return initPromise.then(() =>
            msalInstance.acquireTokenSilent({ ...loginRequest, account: account })
              .then((r) => {
                if (r && r.accessToken) {
                  store.dispatch('setSignininfo', r.accessToken);
                }
                const tk = store.getters.get_Signin_info;
                if (tk) utility.getUserInfo();
                return r;
              })
              .catch(() => msalInstance.loginRedirect(loginRequest))
          );
        }
        return msalInstance
          .acquireTokenRedirect(loginRequest)
          .then((response) => {
            if (response && response.accessToken) {
              store.dispatch('setSignininfo', response.accessToken);
            }
            const tk = store.getters.get_Signin_info;
            if (tk) utility.getUserInfo();
            return response;
          })
          .catch((redirectErr) => {
            return Promise.reject(redirectErr);
          });
      }
      return Promise.reject(err);
    });
}

initPromise.then((response) => {
  if (response) {
    // ----- handleRedirectPromise 成功（登录授权码回调 / 改密码回调 / profile edit 回调等） -----
    // 1) 优先用回调中带回的 accessToken（如果 scope 中包含自定义 API scope 则有值）
    let tokenSaved = false;
    if (response.accessToken) {
      store.dispatch('setSignininfo', response.accessToken);
      tokenSaved = true;
    } else if (response.idToken) {
      // Fallback: 部分环境只有 OIDC scope，accessToken 为空，用 idToken 作为临时 token 尝试 getUserInfo
      store.dispatch('setSignininfo', response.idToken);
      tokenSaved = true;
    }

    // 2) 确保拿到业务 API 级别的 accessToken，并拉取用户信息、店铺数据。
    //    登录回调会把 account 写入 MSAL cache，这里立刻取 account 做一次 acquireTokenSilent：
    //    - 如果 accessToken 本来就有效：命中缓存，毫秒级返回
    //    - 如果只有 OIDC scope，回调中无 accessToken：通过 refresh_token 或缓存换回真正的业务 accessToken
    const accountsAfterRedirect = msalInstance.getAllAccounts();
    const accountToUse = (response && response.account) || (accountsAfterRedirect && accountsAfterRedirect[0]);
    if (accountToUse) {
      // 错误已经在 acquireTokenSilentAndSetup 内部处理，这里仅兜底避免抛未处理 Promise。
      acquireTokenSilentAndSetup(accountToUse).catch(() => {});
    } else if (tokenSaved) {
      // 极端情况：没有 account 但拿到了 accessToken（老版本迁移等），至少填 userInfo
      utility.getUserInfo();
    } else {
      // 连 accessToken 都没有，说明 token 兑换本身可能就有问题
      // 兜底：触发完整登录流程
      try {
        msalInstance.loginRedirect(loginRequest);
      } catch (e) {
        if (e.errorCode !== 'interaction_in_progress') {
          throw e;
        }
      }
    }
  }

  // 回调结束后统一清理 URL 中的 B2C 授权参数，避免刷新时重复触发
  cleanAuthParamsFromUrl();
}).catch((error) => {
  // 先清理 URL 参数，避免用户刷新页面重复报错
  cleanAuthParamsFromUrl();
});

// if the user is already logged in you can acquire a token
// 典型使用场景：
//   - 401 页面 created 发现没登录信息时触发
//   - 其他页面需要主动刷新登录态时触发
// 注意：该函数是兼容式入口，不会与 initPromise/handleRedirectPromise 冲突；
//       如果之前已经在 initPromise 里完成了 acquireTokenSilentAndSetup，这里会命中缓存并快速返回。
msalInstance.handleToken = function () {
  if (!initialized) {
    return initPromise.then(() => {
      return msalInstance.handleToken();
    });
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    return acquireTokenSilentAndSetup(accounts[0]).catch(() => {
      // acquireTokenSilentAndSetup 内部已做回退，这里兜底不再做额外处理
    });
  } else {
    // 没有已登录 account：触发登录重定向
    if (msalInstance.getActiveAccount()) {
      return msalInstance.loginRedirect(loginRequest);
    } else {
      try {
        return msalInstance.loginRedirect(loginRequest);
      } catch (e) {
        if (e.errorCode !== 'interaction_in_progress') {
          throw e;
        }
      }
    }
  }
};

export default msalInstance;
